import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";

// 3rd parties
import { Button, ButtonGroup, Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useWindowSize } from "react-use";
import styled from "styled-components";

// owner
import SntSelect from "@wrappers/ReactSelect/SntSelect";
import organizationClient from "@apis/organizationClient";
import globalSearchClient from "@apis/globalSearchClient";
import useUser from "@hooks/useUser";
import useErrorDialog from "@/cleanup/widgets/dialog/useErrorDialog";

import { PrimaryTag, GreyTag } from "@cleanup/components/TagMore";
import SntOrgBrandIcon from "@widgets/images/SntOrgBrandIcon";
import SntOrgIcon from "@icons/SntOrgIcon";
import SntPartnersIcon from "@icons/SntPartnersIcon";
import SntGlobeIcon from "@icons/SntGlobeIcon";
import SntSupportUserIcon from "@icons/SntSupportUserIcon";
import SntSystemDevIcon from "@cleanup/components/Icons/SntSystemDevIcon";
import HeaderIconButton from "@/containers/AdminLayout/Header/HeaderIconButton";
import { userSelector } from "@redux/selectors";
import { useCustomBranding } from "@/pages/CustomBranding/useCustomBranding";
import SntButton from "@/cleanup/widgets/forms/SntButton";
import SntButtonGroup from "@/cleanup/widgets/forms/SntButtonGroup";
import SntOnOffSwitch from "@/cleanup/wrappers/Switch/SntOnOffSwitch";
import { hasPermission } from "@/hooks/usePermissions";
import { hasFeature } from "@/hooks/useFeatures";
import { UserPermission } from "@/types/generated/UserPermission";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import type { LoginInfo } from "@/types/LoginInfo";
import type { SearchHistoryItem } from "@/containers/AdminLayout/SearchPanel/types";

const PAGE_SIZE = 50;
const PAGE_JUMP = 10;

// Below this viewport width the pill drops its "Partner ▸" breadcrumb prefix and
// shows just the org (the full chain stays in the popover). Keep in sync with
// HEADER_COMPACT_BELOW in Header.tsx.
const HEADER_COMPACT_BELOW = 1500;

// --- Shared styled components ---

interface StyleCustomInfoBtnProps {
  $hoverColor?: string;
}

const StyleCustomInfoBtn = styled.div<StyleCustomInfoBtnProps>`
  button:hover {
    background-color: ${(props) => props.$hoverColor || ""};
  }
`;

// --- Pill variant styled components ---

const PillChevron = styled.svg<{ $open: boolean }>`
  width: 12px;
  height: 12px;
  transition: transform 0.15s ease;
  transform: ${(props) => (props.$open ? "rotate(180deg)" : "rotate(0)")};
`;

// z-index has to clear the parent menu's stacking context. The system menu
// that hosts this org selector renders above Bootstrap's 1050 modal range,
// so 1060 isn't enough — the popover would render on top visually (because
// the portal puts it later in the DOM) but hit-testing would route taps to
// the menu items underneath, focusing them and scrolling the menu.
const PillPanel = styled.div`
  position: fixed;
  z-index: 100000;
  width: 480px;
  max-width: calc(100vw - 16px);
  max-height: calc(100vh - 120px);
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
`;

const PillBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: transparent;
`;

const PillSearchRow = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e4e7ec;
  padding-right: 8px;
  gap: 6px;
`;

const PillSearch = styled.input`
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border: none;
  font-size: 16px;
  outline: none;
  background: transparent;
`;

const PillList = styled.div`
  max-height: min(480px, calc(100vh - 220px));
  overflow-y: auto;
`;

const PillItem = styled.div<{ $selected: boolean; $highlighted?: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  background: ${(props) =>
    props.$highlighted
      ? props.$selected
        ? "#dbe4ff"
        : "#dde4ec"
      : props.$selected
        ? "#eff4ff"
        : "transparent"};

  &:hover {
    background: ${(props) => (props.$selected ? "#dbe4ff" : "#f9fafb")};
  }
`;

const PillItemSub = styled.div`
  font-size: 11px;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PillSectionLabel = styled.div`
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  opacity: 0.5;
  letter-spacing: 0.03em;
`;

const PillRecentSection = styled.div`
  background: #f0f4f8;
`;

const PillDivider = styled.div`
  border-top: 1px solid #e4e7ec;
  margin: 4px 0;
`;

// --- Shared helpers ---

interface OrganizationOption {
  label: string;
  value: number;
  type: string;
  commercialPartner?: boolean;
  partnerName?: string;
  partnerVariant?: "CHANNEL_PARTNER" | "MULTI_ORG";
  partnerType?: string;
  orgCount?: number;
  webDomain?: string;
}

interface OrganizationBasicInfo {
  id: number;
  name: string;
  partnerName?: string;
  organisationType: string;
  commercialPartner?: boolean;
  partnerVariant?: "CHANNEL_PARTNER" | "MULTI_ORG";
  partnerType?: string;
  webDomain?: string;
}

interface Section {
  id: string;
  title: string;
  isShow: boolean;
  icon: React.ReactNode;
}

interface OrgSelectorChangeData {
  value: number;
}

// Brand icon for an org — uses Brandfetch when a webDomain is set, falls back
// to initials. Keeps row identity recognisable at a glance.
const OrgIconDecorator = ({
  option,
  size = 20,
}: {
  option: OrganizationOption;
  size?: number;
}) => (
  <SntOrgBrandIcon domain={option.webDomain} name={option.label} size={size} />
);

// Channel partners show their SF partner type (VAR / MSP / INTEGRATOR / …);
// multi-org partners show a "Multi-org" badge. The old single-letter
// 'C' / 'M' badge was opaque to most colleagues.
// `compact` renders the variant inline as "(label)" text instead of a coloured
// badge, so it sits naturally inside the partner-name sub-text in Org-tab
// dropdown rows (STIC-15255).
const PartnerTypeBadge = ({
  option,
  compact = false,
}: {
  option: OrganizationOption;
  compact?: boolean;
}) => {
  let variantLabel: string | null = null;
  if (option.partnerVariant === "CHANNEL_PARTNER") {
    variantLabel = option.partnerType ?? null;
  } else if (option.partnerVariant === "MULTI_ORG") {
    variantLabel = translate(LanguageKey.multi_org_key);
  }

  if (!variantLabel) return null;

  if (compact) {
    return <span title={variantLabel}>({variantLabel})</span>;
  }

  // Channel partners blue; multi-org stays grey.
  if (option.partnerVariant === "CHANNEL_PARTNER") {
    return <PrimaryTag tagName={variantLabel} size="sm" title={variantLabel} />;
  }
  return <GreyTag tagName={variantLabel} size="sm" title={variantLabel} />;
};

// Compact "P" badge used by the "prefix-icon" variant — overlaid on the
// top-left corner of a partner row's avatar. Only rendered on partners; child
// orgs get no decorator (a badge on every row is visual noise).
const PartnerDecorator = () => (
  <span
    style={{
      position: "absolute",
      top: -4,
      left: -4,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 16,
      height: 16,
      borderRadius: 4,
      background: "#0071a1",
      color: "#ffffff",
      fontSize: 10,
      fontWeight: 700,
      lineHeight: 1,
      boxShadow: "0 0 0 2px #ffffff",
      pointerEvents: "none",
    }}
    title="Partner"
    aria-label="Partner"
  >
    P
  </span>
);

// Avatar + partner decorator, sharing the position-relative wrapper. Used by
// both the dropdown row and the trigger pill so partner rows read the same in
// every surface.
const OrgIconWithPartnerBadge = ({
  option,
  size,
  showPartnerBadge,
}: {
  option: OrganizationOption;
  size: number;
  showPartnerBadge: boolean;
}) => (
  <span
    style={{
      position: "relative",
      display: "inline-flex",
      alignItems: "center",
      lineHeight: 0,
    }}
  >
    <SntOrgBrandIcon domain={option.webDomain} name={option.label} size={size} />
    {showPartnerBadge && <PartnerDecorator />}
  </span>
);

// --- Component ---

interface SntOrgMenuSelectorContainerProps {
  value: number | null;
  onChange?: (data: OrgSelectorChangeData) => void;
  disabled?: boolean;
  variant?: "classic" | "pill";
  hideImpersonate?: boolean;
}

const SntOrgMenuSelectorContainer = ({
  value,
  onChange,
  disabled = false,
  variant = "classic",
  hideImpersonate = false,
}: SntOrgMenuSelectorContainerProps) => {
  const loginInfo = useSelector(userSelector) as LoginInfo;
  const { onImpersonateUser, IMPERSONATE_TYPE } = useUser();
  const { handleOpen: openErrorDialog, ErrorDialog } = useErrorDialog({});
  const { mergeDataWithValueDefault } = useCustomBranding();
  const { width: windowWidth } = useWindowSize();
  const compact = windowWidth < HEADER_COMPACT_BELOW;

  const customStyle = useMemo(() => {
    let custumInfo = {};
    if (loginInfo && loginInfo.custumInfo) {
      custumInfo = loginInfo.custumInfo;
    }
    return mergeDataWithValueDefault(custumInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginInfo]);

  // Org|Partner|All selector (see .snt-org-selector rules in sidebar.scss): every org
  // gets transparent inactive tabs + an active tab filled with the selected-menu-item
  // color (white on the default sidebar, the partner's color when branded). Only a
  // custom-branded org sets an inline text color (its section-font color) and gets the
  // .snt-org-selector-branded text override on the active/hover fill; a non-branded org
  // leaves the text as the greylight navy ($sensolus-primary #212851, i.e. dark blue).
  const hasCustomBranding = !!loginInfo?.custumInfo?.adminSideBarColor;

  const [sections, setSections] = useState<Section[]>([]);
  const [mode, setMode] = useState<string>();

  // Classic variant state
  const [allItems, setAllItems] = useState<OrganizationOption[]>([]);
  const [visibleItems, setVisibleItems] = useState<OrganizationOption[]>([]);
  const searchTextRef = useRef("");

  const [orgList, setOrgList] = useState<OrganizationOption[]>([]);
  const [partnerList, setPartnerList] = useState<OrganizationOption[]>([]);
  const [systemOrg, setSystemOrg] = useState<OrganizationOption | null>(null);
  const orgSelectedRef = useRef<number | undefined>(undefined);
  const partnerSelectedRef = useRef<number | undefined>(undefined);

  const [isLoadingData, setLoadingData] = useState(true);
  const [isFirstLoaded, setFirstLoaded] = useState(true);

  // Pill variant state
  const lastNonSystemValueRef = useRef<number>(0);
  const autoOpenPillRef = useRef(false);
  const [pillOpen, setPillOpen] = useState(false);
  const [pillPos, setPillPos] = useState({ top: 0, left: 0 });
  const pillTriggerRef = useRef<HTMLButtonElement>(null);
  const pillPopoverRef = useRef<HTMLDivElement>(null);
  const pillSearchRef = useRef<HTMLInputElement>(null);
  const [pillSearch, setPillSearch] = useState("");
  const [pillPartnersOnly, setPillPartnersOnly] = useState(false);
  const [pillHighlightIdx, setPillHighlightIdx] = useState(-1);
  const pillListRef = useRef<HTMLDivElement>(null);
  const [recentEntries, setRecentEntries] = useState<Array<{ id: number; type: "PARTNER" | "ORG" }>>([]);

  // --- Shared impersonate handler ---

  const canImpersonate =
    mode !== "ALL" &&
    (hasPermission(UserPermission.ORG_SUPPORT_LOGIN_VIEW_ONLY) ||
      hasPermission(UserPermission.ORG_SUPPORT_LOGIN_ADMIN)) &&
    hasFeature(FeatureKey.impersonate);

  const impersonateTarget = useMemo(() => {
    if (!value) return null;
    return [...orgList, ...partnerList].find((o) => o.value === value) || null;
  }, [value, orgList, partnerList]);

  const handleImpersonate = useCallback(() => {
    if (value === null) return;
    let impersonateMode = IMPERSONATE_TYPE.VIEWER;
    if (!hasPermission(UserPermission.ORG_SUPPORT_LOGIN_VIEW_ONLY)) {
      impersonateMode = IMPERSONATE_TYPE.ADMIN;
    }
    onImpersonateUser({
      id: value,
      type: impersonateMode,
      onBadRequest: () => {
        openErrorDialog(translate(LanguageKey.impersonation_insufficient_rights_key));
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const renderImpersonateButton = () => {
    if (!canImpersonate) return null;
    const isTargetSystem = impersonateTarget?.type === "SYSTEM";
    const title = isTargetSystem
      ? translate(LanguageKey.support_login_on_system_account_is_not_permitted_key)
      : translate(LanguageKey.support_login_key);
    const icon = <SntSupportUserIcon color="rgb(255, 168, 88)" style={{ opacity: isTargetSystem ? 0.4 : 1 }} />;
    const handleClick = isTargetSystem ? () => {} : handleImpersonate;

    if (variant === "pill") {
      return (
        <HeaderIconButton title={title} onClick={handleClick}>
          {icon}
        </HeaderIconButton>
      );
    }

    return (
      <Button
        variant="sensolus-greylight"
        disabled={isTargetSystem}
        className="ms-1"
        style={{
          color: customStyle.adminSideBarSectionFontColor,
          borderColor: customStyle.adminSideBarSectionFontColor,
        }}
        title={isTargetSystem ? title : undefined}
        onClick={handleClick}
      >
        {icon}
      </Button>
    );
  };

  // Closing on outside-click is handled by <PillBackdrop> inside the portal —
  // no document-level listener (those race with focus on mobile and tend to
  // close the popover the instant the user taps the search input).

  // Fetch recently viewed orgs/partners when pill opens
  useEffect(() => {
    if (variant !== "pill" || !pillOpen) return;
    globalSearchClient
      .getSearchHistory()
      .then(({ data }: { data: SearchHistoryItem[] }) => {
        // Preserve backend order (lastSeen DESC) so the merged recent list blends
        // partners and orgs by timestamp instead of grouping partners first.
        const entries: Array<{ id: number; type: "PARTNER" | "ORG" }> = [];
        for (const item of data) {
          const cat = item.searchIndex.category;
          const id = Number(item.searchIndex.entityId);
          if (cat === "Organization") {
            // Orgs may also appear as partners in the pill
            entries.push({ id, type: partnerList.some((p) => p.value === id) ? "PARTNER" : "ORG" });
          } else if (cat === "Partner") {
            entries.push({ id, type: "PARTNER" });
          }
        }
        setRecentEntries(entries);
      })
      .catch(() => {
        // silently ignore - recents are non-critical
      });
  }, [variant, pillOpen, partnerList]);

  // After the SYSTEM→Organizations toggle re-mounts the pill, open the popover
  // anchored to its now-visible trigger. Has to wait for the trigger ref to
  // populate — toggling state alone doesn't give us a layout pass.
  useEffect(() => {
    if (variant !== "pill" || !autoOpenPillRef.current || mode === "SYSTEM" || !mode) return;
    if (!pillTriggerRef.current) return;
    autoOpenPillRef.current = false;
    const rect = pillTriggerRef.current.getBoundingClientRect();
    const left = Math.min(rect.left, window.innerWidth - 488);
    setPillPos({ top: rect.bottom + 4, left: Math.max(8, left) });
    setPillSearch("");
    setPillPartnersOnly(false);
    setPillOpen(true);
  }, [variant, mode]);

  // Focus the search input when the popover opens. Critical because the
  // popover is portaled to document.body — without explicit focus, Tab from
  // the trigger walks through every focusable element on the page before
  // reaching the popover (which lives at the end of the DOM).
  useEffect(() => {
    if (!pillOpen) return;
    const id = setTimeout(() => pillSearchRef.current?.focus(), 50);
    return () => clearTimeout(id);
  }, [pillOpen]);

  // Unified list: partners + orgs merged alphabetically so a single search box
  // matches either. The PARTNER/ORG tab split is gone — type is conveyed by the
  // row's badge (partner) or partner-context sub-text (org).
  const pillCombinedItems = useMemo(() => {
    if (variant !== "pill") return [];
    return [...partnerList, ...orgList].sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase())
    );
  }, [variant, partnerList, orgList]);

  const pillFilteredItems = useMemo(() => {
    if (variant !== "pill") return [];
    // When "Partners only" is on, drop non-partner rows.
    const base = pillPartnersOnly
      ? pillCombinedItems.filter((item) => item.type === "PARTNER")
      : pillCombinedItems;
    if (!pillSearch) return base.slice(0, PAGE_SIZE);
    const lower = pillSearch.toLowerCase();
    return base
      .filter((item) => item.label.toLowerCase().includes(lower))
      .slice(0, PAGE_SIZE);
  }, [variant, pillCombinedItems, pillSearch, pillPartnersOnly]);

  const pillRecentItems = useMemo(() => {
    if (variant !== "pill" || pillSearch) return [];
    if (recentEntries.length === 0) return [];
    const seen = new Set<number>();
    const items: OrganizationOption[] = [];
    for (const { id } of recentEntries) {
      if (seen.has(id)) continue;
      seen.add(id);
      if (id === value) continue; // skip the currently selected scope — already shown in the pill
      const found = pillCombinedItems.find((o) => o.value === id);
      if (!found) continue;
      if (pillPartnersOnly && found.type !== "PARTNER") continue;
      items.push(found);
    }
    return items.slice(0, 5);
  }, [variant, pillSearch, recentEntries, pillCombinedItems, value, pillPartnersOnly]);

  // Pseudo "All" entry pinned at the top of the unified list — selecting it
  // sets value=0 ("across all organizations"). Globe icon signals this is a
  // special scope, not a real org row.
  const allPseudoItem = useMemo<OrganizationOption>(
    () => ({
      label: translate(LanguageKey.all_organizations_key),
      value: 0,
      type: "ALL",
    }),
    []
  );

  const showAllPseudoItem = useMemo(() => {
    if (pillPartnersOnly) return false;
    if (!pillSearch) return true;
    return allPseudoItem.label.toLowerCase().includes(pillSearch.toLowerCase());
  }, [pillSearch, allPseudoItem, pillPartnersOnly]);

  // Combined list of all navigable items for keyboard navigation
  const pillAllNavigableItems = useMemo(() => {
    const items: OrganizationOption[] = [];
    if (showAllPseudoItem) items.push(allPseudoItem);
    items.push(...pillRecentItems, ...pillFilteredItems);
    return items;
  }, [showAllPseudoItem, allPseudoItem, pillRecentItems, pillFilteredItems]);

  // Reset highlight when search text or partners-only filter changes
  useEffect(() => {
    setPillHighlightIdx(-1);
  }, [pillSearch, pillPartnersOnly]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (pillHighlightIdx < 0 || !pillListRef.current) return;
    const items = pillListRef.current.querySelectorAll("[data-pill-item]");
    items[pillHighlightIdx]?.scrollIntoView({ block: "nearest" });
  }, [pillHighlightIdx]);

  const handlePillSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const itemCount = pillAllNavigableItems.length;
    if (itemCount === 0) return;

    if (e.key === "ArrowDown" || (e.key === "Tab" && !e.shiftKey)) {
      e.preventDefault();
      setPillHighlightIdx((prev) => (prev < itemCount - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setPillHighlightIdx((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === "PageDown") {
      e.preventDefault();
      setPillHighlightIdx((prev) => Math.min(itemCount - 1, Math.max(prev, 0) + PAGE_JUMP));
    } else if (e.key === "PageUp") {
      e.preventDefault();
      setPillHighlightIdx((prev) => Math.max(0, Math.max(prev, 0) - PAGE_JUMP));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (pillHighlightIdx >= 0 && pillHighlightIdx < itemCount) {
        handlePillItemClick(pillAllNavigableItems[pillHighlightIdx]);
      } else if (itemCount === 1) {
        handlePillItemClick(pillAllNavigableItems[0]);
      }
    } else if (e.key === "Escape") {
      setPillOpen(false);
    }
  };

  // --- Sync mode from value ---

  useEffect(() => {
    if (isLoadingData) return;
    if (!value) {
      setMode("ALL");
      lastNonSystemValueRef.current = 0;
    } else if (systemOrg && systemOrg.value === value) {
      setMode("SYSTEM");
    } else {
      lastNonSystemValueRef.current = value;
      const find = orgList.find((data) => data.value === value);
      if (find) {
        orgSelectedRef.current = value;
        setMode("ORG");
        searchTextRef.current = "";
        setAllItems(orgList);
      } else {
        const partnerFind = partnerList.find((data) => data.value === value);
        if (partnerFind) {
          partnerSelectedRef.current = value;
          setMode("PARTNER");
          searchTextRef.current = "";
          setAllItems(partnerList);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isLoadingData]);

  // --- Load org data ---

  useEffect(() => {
    let existOrg: OrganizationOption | undefined = undefined;
    if (value) {
      existOrg = orgList.find((data) => data.value === value);
      if (!existOrg) {
        existOrg = partnerList.find((data) => data.value === value);
      }
      if (!existOrg && systemOrg?.value === value) {
        existOrg = systemOrg;
      }
      if (!existOrg) {
        setLoadingData(true);
        getOrgList();
      }
    } else if (isFirstLoaded) {
      getOrgList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const getOrgList = () => {
    organizationClient
      .getBasicInfo()
      .then(({ data }: { data: OrganizationBasicInfo[] }) => {
        const listOrg: OrganizationOption[] = [];
        const listPartner: OrganizationOption[] = [];
        let sysOrg: OrganizationOption | null = null;
        data.forEach((item) => {
          const option: OrganizationOption = {
            label: item.name,
            value: item.id,
            type: item.organisationType,
            commercialPartner: item.commercialPartner,
            partnerName: item.partnerName,
            partnerVariant: item.partnerVariant,
            partnerType: item.partnerType,
            webDomain: item.webDomain,
          };
          if (option.type === "SYSTEM") {
            sysOrg = option;
          } else if (option.type === "PARTNER") {
            option.label = item.partnerName || item.name;
            listPartner.push(option);
          } else {
            listOrg.push(option);
          }
        });

        const orgCountByPartner: Record<string, number> = {};
        listOrg.forEach((org) => {
          if (org.partnerName) {
            orgCountByPartner[org.partnerName] = (orgCountByPartner[org.partnerName] || 0) + 1;
          }
        });
        listPartner.forEach((partner) => {
          partner.orgCount = orgCountByPartner[partner.label] || 0;
        });

        setOrgList(listOrg);
        setPartnerList(listPartner);
        setSystemOrg(sysOrg);
        const sectionItems: Section[] = [
          { id: "ORG", title: translate(LanguageKey.org_key), isShow: listOrg.length !== 0, icon: <SntOrgIcon /> },
          { id: "PARTNER", title: translate(LanguageKey.part_key), isShow: listPartner.length !== 0, icon: <SntPartnersIcon /> },
          { id: "ALL", title: translate(LanguageKey.all_key), isShow: true, icon: <SntGlobeIcon /> },
          { id: "SYSTEM", title: translate(LanguageKey.system_key), isShow: sysOrg !== null, icon: <SntSystemDevIcon /> },
        ];
        if (loginInfo.organisation?.organisationType === "SYSTEM") {
          sectionItems.reverse();
        }
        setSections(sectionItems);
        setLoadingData(false);
        setFirstLoaded(false);
      })
      .catch(() => {
        setLoadingData(false);
        setFirstLoaded(false);
      });
  };

  // --- Classic variant helpers ---

  const getFilteredItems = useCallback(
    (sourceItems: OrganizationOption[], searchText: string) => {
      if (!searchText) return sourceItems;
      const lower = searchText.toLowerCase();
      return sourceItems.filter((item) => item.label.toLowerCase().includes(lower));
    },
    []
  );

  useEffect(() => {
    const filtered = getFilteredItems(allItems, searchTextRef.current);
    setVisibleItems(filtered.slice(0, PAGE_SIZE));
  }, [allItems, getFilteredItems]);

  const onMenuScrollToBottom = useCallback(() => {
    const filtered = getFilteredItems(allItems, searchTextRef.current);
    if (visibleItems.length < filtered.length) {
      setVisibleItems(filtered.slice(0, visibleItems.length + PAGE_SIZE));
    }
  }, [allItems, visibleItems.length, getFilteredItems]);

  const onInputChange = useCallback(
    (text: string) => {
      searchTextRef.current = text;
      const filtered = getFilteredItems(allItems, text);
      setVisibleItems(filtered.slice(0, PAGE_SIZE));
    },
    [allItems, getFilteredItems]
  );

  const selectChanged = (item: OrganizationOption) => {
    onChange?.({ value: item.value });
  };

  // --- Pill variant helpers ---

  const pillLabel = useMemo(() => {
    if (mode === "ALL") return translate(LanguageKey.all_key);
    if (mode === "SYSTEM") return translate(LanguageKey.system_key);
    if (mode === "PARTNER") {
      const found = partnerList.find((d) => d.value === value);
      return found?.label || translate(LanguageKey.all_key);
    }
    const found = orgList.find((d) => d.value === value);
    return found?.label || translate(LanguageKey.all_key);
  }, [mode, value, partnerList, orgList]);

  const pillPartnerContext = useMemo(() => {
    if (mode !== "ORG") return null;
    const found = orgList.find((d) => d.value === value);
    if (!found?.partnerName) return null;
    return found;
  }, [mode, value, orgList]);

  // Bordered white box wrapping a glyph — same look as the "All organizations"
  // / "System" dropdown rows. Keeps the pill trigger visually consistent with
  // the row that selected it.
  const PillIconBox = ({ children }: { children: React.ReactNode }) => (
    <span
      style={{
        width: 28,
        height: 28,
        background: "#ffffff",
        border: "1px solid #b8bfca",
        borderRadius: 6,
        boxSizing: "border-box",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#1d283c",
        flexShrink: 0,
      }}
    >
      {children}
    </span>
  );

  const pillIcon = useMemo(() => {
    if (mode === "ALL") return <PillIconBox><SntGlobeIcon /></PillIconBox>;
    if (mode === "SYSTEM") return <SntSystemDevIcon />;
    // For PARTNER and ORG modes the brand-icon box is rendered inside
    // renderPillLabel via OrgIconDecorator — matching how dropdown rows look.
    return null;
  }, [mode]);

  const handlePillTriggerClick = () => {
    if (disabled) return;
    if (!pillOpen && pillTriggerRef.current) {
      const rect = pillTriggerRef.current.getBoundingClientRect();
      const left = Math.min(rect.left, window.innerWidth - 488);
      setPillPos({ top: rect.bottom + 4, left: Math.max(8, left) });
      setPillSearch("");
      setPillPartnersOnly(false);
    }
    setPillOpen((prev) => !prev);
  };

  const handlePillItemClick = (item: OrganizationOption) => {
    setPillOpen(false);
    setPillSearch("");
    onChange?.({ value: item.value });
  };

  // --- Pill trigger label ---

  const selectedOption = useMemo(() => {
    if (value === null || value === 0) return null;
    return (
      orgList.find((d) => d.value === value) ||
      partnerList.find((d) => d.value === value) ||
      (systemOrg && systemOrg.value === value ? systemOrg : null)
    );
  }, [value, orgList, partnerList, systemOrg]);

  const renderPillLabel = () => {
    // System has its own <> pillIcon — the brand-icon box would just add
    // meaningless "SA" initials next to it.
    // Trigger pill deliberately does NOT show the "P" partner decorator on the
    // avatar — a single-scope trigger already tells you what you're looking at,
    // and the badge just added noise (see feedback on org-switcher redesign).
    const orgIcon = selectedOption && mode !== "SYSTEM" ? (
      <span className="me-1 d-inline-flex align-items-center">
        <OrgIconDecorator option={selectedOption} size={28} />
      </span>
    ) : null;
    if (pillPartnerContext) {
      // The partner-name prefix ("Partner ▸") is the first thing to drop when the
      // header runs out of room — when compact we show just the org (the full chain
      // is still available inside the popover). Otherwise the whole breadcrumb shows.
      return (
        <>
          {!compact && (
            <span className="d-inline-flex align-items-center gap-1">
              {pillPartnerContext.partnerName}{" "}
              <PartnerTypeBadge option={pillPartnerContext} />
              {" › "}
            </span>
          )}
          {orgIcon}
          {pillLabel}
        </>
      );
    }
    if (mode === "PARTNER") {
      const found = partnerList.find((d) => d.value === value);
      return (
        <span className="d-inline-flex align-items-center gap-2">
          {found && <OrgIconDecorator option={found} size={28} />}
          <span>{pillLabel}</span>
          {found && <PartnerTypeBadge option={found} />}
        </span>
      );
    }
    return (
      <>
        {orgIcon}
        {pillLabel}
      </>
    );
  };

  // --- Pill popover content ---

  const renderPillItem = (item: OrganizationOption, navIndex: number, keyPrefix = "") => {
    const isAll = item.type === "ALL";
    const isPartner = item.type === "PARTNER";
    const isSelected = isAll
      ? value === 0 || value === null
      : item.value === value;
    const isHighlighted = navIndex === pillHighlightIdx;

    // A partner row is signalled by the blue "P" badge decorator on the avatar
    // (see OrgIconWithPartnerBadge below). The P already says "partner", so the
    // right-hand chip shows only the variant (Multi-org / VAR / ...).
    const showPartnerDecorator = isPartner;

    return (
      <PillItem
        key={`${keyPrefix}${item.value}`}
        data-pill-item
        $selected={isSelected}
        $highlighted={isHighlighted}
        onClick={() => handlePillItemClick(item)}
        onMouseEnter={() => setPillHighlightIdx(navIndex)}
      >
        <div className="flex-shrink-0 me-2 d-flex align-items-center">
          {isAll ? (
            <span
              style={{
                width: 32,
                height: 32,
                background: "#ffffff",
                border: "1px solid #b8bfca",
                borderRadius: 6,
                boxSizing: "border-box",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#1d283c",
              }}
              aria-label="All organizations"
            >
              <SntGlobeIcon />
            </span>
          ) : (
            <OrgIconWithPartnerBadge
              option={item}
              size={32}
              showPartnerBadge={showPartnerDecorator}
            />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="d-flex align-items-center gap-2">
            <span className={`text-truncate ${isSelected ? "fw-bold" : ""}`}>
              {item.label}
            </span>
            {isPartner && (
              <div className="flex-shrink-0">
                <PartnerTypeBadge option={item} />
              </div>
            )}
          </div>
          {!isAll && isPartner && item.orgCount ? (
            <PillItemSub>
              {`${item.orgCount} ${item.orgCount === 1 ? translate(LanguageKey.organization_key).toLowerCase() : translate(LanguageKey.organizations_key).toLowerCase()}`}
            </PillItemSub>
          ) : !isAll && !isPartner ? (
            <PillItemSub className="d-flex align-items-center gap-1">
              <span className="text-truncate" style={{ minWidth: 0 }}>
                {item.partnerName || "Sensolus only"}
              </span>
              {item.partnerName && (
                <span className="flex-shrink-0">
                  <PartnerTypeBadge option={item} compact />
                </span>
              )}
            </PillItemSub>
          ) : null}
        </div>
      </PillItem>
    );
  };

  const renderPillPopoverContent = () => {
    const allItemOffset = showAllPseudoItem ? 1 : 0;
    return (
      <>
        <PillSearchRow>
          <PillSearch
            ref={pillSearchRef}
            placeholder={`${translate(LanguageKey.search_key)}...`}
            value={pillSearch}
            onChange={(e) => setPillSearch(e.target.value)}
            onKeyDown={handlePillSearchKeyDown}
          />
          <SntOnOffSwitch
            checked={pillPartnersOnly}
            onChange={setPillPartnersOnly}
            title={translate(LanguageKey.partners_only_key)}
          />
        </PillSearchRow>
        <PillList ref={pillListRef}>
          {showAllPseudoItem && renderPillItem(allPseudoItem, 0, "all-")}
          {showAllPseudoItem &&
            (pillRecentItems.length > 0 || pillFilteredItems.length > 0) && <PillDivider />}
          {pillRecentItems.length > 0 && (
            <PillRecentSection>
              <PillSectionLabel>{translate(LanguageKey.v41_recent_viewed_key)}</PillSectionLabel>
              {pillRecentItems.map((item, i) =>
                renderPillItem(item, allItemOffset + i, "recent-")
              )}
            </PillRecentSection>
          )}
          {pillRecentItems.length > 0 && pillFilteredItems.length > 0 && <PillDivider />}
          {pillFilteredItems.map((item, i) =>
            renderPillItem(item, allItemOffset + pillRecentItems.length + i)
          )}
        </PillList>
      </>
    );
  };

  const renderPillPopover = () => {
    if (!pillOpen) return null;
    return createPortal(
      <>
        <PillBackdrop
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setPillOpen(false);
          }}
        />
        <PillPanel
          ref={pillPopoverRef}
          data-pill-popover
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{ top: pillPos.top, left: pillPos.left }}
        >
          {renderPillPopoverContent()}
        </PillPanel>
      </>,
      document.body
    );
  };

  // --- Render mode switcher ---

  const renderModeSwitcher = () => {
    if (variant === "pill") {
      return (
        <div className="d-flex flex-wrap align-items-center gap-2">
          {systemOrg && (
            <SntButtonGroup<"ORG" | "SYSTEM">
              value={mode === "SYSTEM" ? "SYSTEM" : "ORG"}
              onChange={(next) => {
                if (next === "SYSTEM") {
                  setPillOpen(false);
                  onChange?.({ value: systemOrg.value });
                } else {
                  autoOpenPillRef.current = true;
                  onChange?.({ value: lastNonSystemValueRef.current });
                }
              }}
              options={[
                { value: "SYSTEM", label: translate(LanguageKey.system_key) },
                { value: "ORG", label: translate(LanguageKey.organizations_key) },
              ]}
            />
          )}
          {mode !== "SYSTEM" && (
            <Button
              ref={pillTriggerRef}
              variant="sensolus-greylight"
              onClick={handlePillTriggerClick}
              disabled={disabled}
              className="d-inline-flex align-items-center gap-2"
              style={{
                height: 36,
                padding: "0 12px",
                fontSize: 14,
                whiteSpace: "nowrap",
                position: pillOpen ? "relative" : undefined,
                zIndex: pillOpen ? 1061 : undefined,
              }}
            >
              {pillIcon}
              {renderPillLabel()}
              <PillChevron $open={pillOpen} viewBox="0 0 12 12">
                <path d="M2.5 4.5L6 8L9.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </PillChevron>
            </Button>
          )}
          {mode !== "SYSTEM" && !hideImpersonate && renderImpersonateButton()}
          {renderPillPopover()}
        </div>
      );
    }

    // Classic ButtonGroup
    return (
      <ButtonGroup
        style={{ width: "100%" }}
        className={`snt-org-selector${hasCustomBranding ? " snt-org-selector-branded" : ""}`}
      >
        {sections
          .filter((item) => item.isShow)
          .map((item, itemIdx) => (
            <SntButton
              key={itemIdx}
              style={{
                color: hasCustomBranding ? customStyle.adminSideBarSectionFontColor : undefined,
                borderColor: hasCustomBranding ? customStyle.adminSideBarSectionFontColor : undefined,
                padding: "0.375rem",
              }}
              className={`${item.id === mode ? "active" : ""}`}
              onClick={() => {
                let val = 0;
                if (item.id === "ORG") {
                  val = orgSelectedRef.current || (orgList[0] ? orgList[0].value : 0);
                } else if (item.id === "PARTNER") {
                  val = partnerSelectedRef.current || (partnerList[0] ? partnerList[0].value : 0);
                } else if (item.id === "SYSTEM" && systemOrg) {
                  val = systemOrg.value;
                }
                onChange?.({ value: val });
              }}
              icon={item.icon}
              text={item.title}
            />
          ))}
      </ButtonGroup>
    );
  };

  // --- Classic variant: formatOptionLabel for SntSelect ---

  const isSystemUser = loginInfo.organisation?.organisationType === "SYSTEM";

  const formatOptionLabel = useCallback(
    (option: OrganizationOption, { context }: { context: string }) => {
      if (context === "value") {
        if (mode === "ORG" && option.partnerName && isSystemUser) {
          return (
            <span className="d-inline-flex align-items-center gap-2">
              <span style={{ opacity: 0.6 }}>{option.partnerName}</span>
              <PartnerTypeBadge option={option} />
              <span style={{ opacity: 0.4 }}>›</span>
              <span>{option.label}</span>
            </span>
          );
        }
        if (mode === "PARTNER") {
          return (
            <span className="d-inline-flex align-items-center gap-2">
              <span>{option.label}</span>
              <PartnerTypeBadge option={option} />
            </span>
          );
        }
        return option.label;
      }
      if (!isSystemUser) return option.label;
      if (mode === "PARTNER") {
        return (
          <div className="d-flex align-items-center justify-content-between">
            <div className="text-truncate">
              <div className="text-truncate">{option.label}</div>
              <div style={{ fontSize: 11, opacity: 0.7 }} className="text-truncate">
                {option.orgCount
                  ? `${option.orgCount} ${option.orgCount === 1 ? translate(LanguageKey.organization_key).toLowerCase() : translate(LanguageKey.organizations_key).toLowerCase()}`
                  : ""}
              </div>
            </div>
            <div className="flex-shrink-0 ms-2"><PartnerTypeBadge option={option} /></div>
          </div>
        );
      }
      return (
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-truncate">
            <div className="text-truncate">{option.label}</div>
            <div
              style={{ fontSize: 11, opacity: 0.7 }}
              className="text-truncate d-flex align-items-center gap-1"
            >
              <span className="text-truncate" style={{ minWidth: 0 }}>
                {option.partnerName || "Sensolus only"}
              </span>
              {option.partnerName && (
                <span className="flex-shrink-0"><PartnerTypeBadge option={option} compact /></span>
              )}
            </div>
          </div>
        </div>
      );
    },
    [mode, isSystemUser]
  );

  if (variant === "pill") {
    return (
      <>
        {renderModeSwitcher()}
        <ErrorDialog />
      </>
    );
  }

  return (
    <StyleCustomInfoBtn $hoverColor={customStyle.adminSideBarSelectedColor}>
      <Row>
        <Col xs={12}>
          {renderModeSwitcher()}
        </Col>
        {(mode === "ORG" ||
            (mode === "PARTNER" && loginInfo.organisation?.organisationType !== "PARTNER")) && (
          <Col xs={12} className="mt-2">
            <div className="d-flex justify-content-between w-100">
              <div className="flex-grow-1 flex-shrink-1" style={{ minWidth: 0 }}>
                <SntSelect
                  onChange={selectChanged}
                  options={visibleItems}
                  value={allItems.filter((option) => option.value === value)}
                  isDisabled={disabled}
                  filterOption={null}
                  onInputChange={onInputChange}
                  onMenuScrollToBottom={onMenuScrollToBottom}
                  styles={{
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    menu: (base: any) => ({
                      ...base,
                      ...(isSystemUser && {
                        width: mode === "PARTNER" ? "max(100%, 350px)" : "max(100%, 300px)",
                      }),
                    }),
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ...(isSystemUser && { option: (base: any) => ({ ...base, padding: "6px 12px" }) }),
                  }}
                  formatOptionLabel={formatOptionLabel}
                />
              </div>
              {renderImpersonateButton()}
            </div>
          </Col>
        )}
      </Row>
      <ErrorDialog />
    </StyleCustomInfoBtn>
  );
};

export default SntOrgMenuSelectorContainer;
